import React, { useState, useEffect, useRef } from 'react';
import { formatCoordinates, parseCoordinates } from '../utils/coordinateUtils';

/**
 * Mobile-optimized interactive Map Coordinate Picker.
 * Uses OpenStreetMap tiles with Leaflet, draggable map pin,
 * place search (Nominatim), and mobile GPS device location.
 */
export default function MapCoordinatePicker({
  isOpen,
  onClose,
  onSelect,
  initialValue = '',
  fieldName = 'Coordinates',
  format = 'dd'
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);

  // Parse initial coordinates if provided
  const parsedInitial = parseCoordinates(initialValue);
  const defaultLat = parsedInitial ? parsedInitial.lat : -17.8252; // Default southern Africa / global sensible fallback
  const defaultLng = parsedInitial ? parsedInitial.lng : 31.0335;
  const initialZoom = parsedInitial ? 14 : 4;

  const [currentCoords, setCurrentCoords] = useState({
    lat: defaultLat,
    lng: defaultLng
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [leafletLoaded, setLeafletLoaded] = useState(!!window.L);
  const [leafletLoadError, setLeafletLoadError] = useState(false);

  // Sync state if initial coordinates change
  useEffect(() => {
    if (parsedInitial) {
      setCurrentCoords({ lat: parsedInitial.lat, lng: parsedInitial.lng });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  // Load Leaflet JS & CSS dynamically if not already present
  useEffect(() => {
    if (!isOpen) return;
    if (window.L) {
      setLeafletLoaded(true);
      return;
    }

    // 1. Add CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    // 2. Add Script
    if (!document.getElementById('leaflet-js')) {
      const script = document.createElement('script');
      script.id = 'leaflet-js';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.crossOrigin = '';
      script.async = true;
      script.onload = () => {
        setLeafletLoaded(true);
      };
      script.onerror = () => {
        console.warn('Could not load Leaflet from CDN. Falling back to manual input.');
        setLeafletLoadError(true);
      };
      document.head.appendChild(script);
    } else {
      // Script tag exists, wait for window.L
      const interval = setInterval(() => {
        if (window.L) {
          setLeafletLoaded(true);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  // Initialize Leaflet map once loaded and container is ready
  useEffect(() => {
    if (!isOpen || !leafletLoaded || !mapContainerRef.current || !window.L) return;

    const L = window.L;

    // Custom retina-sharp SVG map pin (zero missing image asset issues)
    const pinIcon = L.divIcon({
      className: 'trophy-map-pin',
      html: `
        <div style="
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          transform: translate(-50%, -100%);
          pointer-events: none;
        ">
          <div style="
            background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
            width: 38px;
            height: 38px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 4px 14px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.3);
            border: 2px solid #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              width: 14px;
              height: 14px;
              background: #ffffff;
              border-radius: 50%;
              transform: rotate(45deg);
              box-shadow: 0 1px 3px rgba(0,0,0,0.2);
            "></div>
          </div>
          <div style="
            width: 12px;
            height: 4px;
            background: rgba(0,0,0,0.35);
            border-radius: 50%;
            margin-top: 3px;
            filter: blur(1.5px);
          "></div>
        </div>
      `,
      iconSize: [0, 0],
      iconAnchor: [0, 0]
    });

    // Cleanup previous map if any
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    try {
      const map = L.map(mapContainerRef.current, {
        center: [currentCoords.lat, currentCoords.lng],
        zoom: initialZoom,
        zoomControl: false,
        attributionControl: false
      });

      // High quality OpenStreetMap tiles
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap'
      }).addTo(map);

      // Add zoom control at top-right
      L.control.zoom({ position: 'topright' }).addTo(map);

      // Create draggable pin marker
      const marker = L.marker([currentCoords.lat, currentCoords.lng], {
        icon: pinIcon,
        draggable: true,
        autoPan: true
      }).addTo(map);

      // Map tap/click moves marker
      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        marker.setLatLng([lat, lng]);
        setCurrentCoords({ lat, lng });
      });

      // Marker drag updates coordinates
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        setCurrentCoords({ lat: pos.lat, lng: pos.lng });
      });

      mapInstanceRef.current = map;
      markerInstanceRef.current = marker;

      // Invalidate map size after DOM mount and layout animation
      const timer = setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 250);

      return () => {
        clearTimeout(timer);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
          markerInstanceRef.current = null;
        }
      };
    } catch (err) {
      console.warn('Error initializing Leaflet map:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, leafletLoaded]);

  // Center map on user's mobile GPS device location
  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your mobile browser');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCurrentCoords({ lat, lng });

        if (mapInstanceRef.current && markerInstanceRef.current) {
          markerInstanceRef.current.setLatLng([lat, lng]);
          mapInstanceRef.current.flyTo([lat, lng], 15, { duration: 1.2 });
        }
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        let msg = 'Could not acquire location.';
        if (error.code === 1) msg = 'Location permission denied. Please allow location access in your browser settings.';
        else if (error.code === 2) msg = 'Location unavailable. Please select your location manually on the map.';
        else if (error.code === 3) msg = 'Location request timed out. Please try again.';
        alert(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Search places / addresses via OpenStreetMap Nominatim
  const handleSearchSubmit = async (e) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;

    setIsSearching(true);
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`
      );
      const data = await resp.json();
      setSearchResults(data || []);
      if (!data || data.length === 0) {
        alert(`No locations found for "${q}". Try a nearby town, landmark, or water body.`);
      }
    } catch (err) {
      console.warn('Location search error:', err);
      alert('Could not perform location search. Please tap directly on the map.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setCurrentCoords({ lat, lng });
    setSearchResults([]);
    setSearchQuery(result.display_name.split(',')[0]);

    if (mapInstanceRef.current && markerInstanceRef.current) {
      markerInstanceRef.current.setLatLng([lat, lng]);
      mapInstanceRef.current.flyTo([lat, lng], 14, { duration: 1.2 });
    }
  };

  // Confirm selection
  const handleConfirm = () => {
    const formatted = formatCoordinates(currentCoords.lat, currentCoords.lng, format);
    onSelect(formatted);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        backgroundColor: 'rgba(7, 9, 14, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontFamily: 'inherit'
      }}
      data-testid="map-coordinate-picker-modal"
    >
      {/* 1. Modal Top Bar */}
      <div
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--panel-bg, #0f172a)',
          borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))',
          flexShrink: 0
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main, #f8fafc)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🗺️</span>
            <span>Pin Location on Map</span>
          </h3>
          <div style={{ fontSize: '11px', color: 'var(--text-mute, #94a3b8)', marginTop: '2px' }}>
            Tap map or drag the pin to set {fieldName}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close Map"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: 'none',
            color: 'var(--text-main, #cbd5e1)',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          ✕
        </button>
      </div>

      {/* 2. Search Bar */}
      <div
        style={{
          position: 'relative',
          padding: '8px 16px',
          background: 'var(--card-bg, #0b1120)',
          borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.08))',
          flexShrink: 0
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search town, lake, river, or landmark..."
              style={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px',
                padding: '8px 12px',
                paddingRight: searchQuery ? '32px' : '12px',
                fontSize: '14px',
                color: '#fff',
                outline: 'none'
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                ✕
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={isSearching}
            style={{
              backgroundColor: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '0 14px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap'
            }}
          >
            {isSearching ? '...' : 'Search'}
          </button>
        </form>

        {/* Search Results Dropdown Overlay */}
        {searchResults.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '16px',
              right: '16px',
              backgroundColor: '#1e293b',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
              zIndex: 1000,
              maxHeight: '220px',
              overflowY: 'auto'
            }}
          >
            {searchResults.map((res, idx) => (
              <div
                key={idx}
                onClick={() => handleSelectSearchResult(res)}
                style={{
                  padding: '10px 12px',
                  borderBottom: idx === searchResults.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  color: '#f1f5f9',
                  fontSize: '13px',
                  cursor: 'pointer',
                  lineHeight: '1.4'
                }}
              >
                <div style={{ fontWeight: 600, color: '#38bdf8' }}>{res.display_name.split(',')[0]}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {res.display_name}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3. Map Viewport or Fallback Container */}
      <div style={{ position: 'relative', flex: 1, width: '100%', minHeight: 0 }}>
        {leafletLoadError ? (
          /* Manual Input Fallback if Leaflet CDN is blocked / offline */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              color: '#cbd5e1',
              height: '100%',
              boxSizing: 'border-box',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📍</div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#f8fafc', marginBottom: '8px' }}>
              Map Unavailable (Offline)
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', maxWidth: '300px', marginBottom: '16px' }}>
              You can acquire GPS coordinates directly from your mobile device, or enter latitude and longitude manually.
            </div>

            <button
              type="button"
              onClick={handleLocateMe}
              disabled={isLocating}
              style={{
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              {isLocating ? 'Acquiring GPS...' : '📍 Use Mobile Device GPS'}
            </button>

            <div style={{ display: 'flex', gap: '8px', maxWidth: '320px', width: '100%' }}>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <label style={{ fontSize: '11px', color: '#94a3b8' }}>Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={currentCoords.lat}
                  onChange={(e) => setCurrentCoords(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '6px',
                    padding: '8px',
                    color: '#fff'
                  }}
                />
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <label style={{ fontSize: '11px', color: '#94a3b8' }}>Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={currentCoords.lng}
                  onChange={(e) => setCurrentCoords(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '6px',
                    padding: '8px',
                    color: '#fff'
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          /* Interactive Leaflet Map */
          <>
            <div
              ref={mapContainerRef}
              data-testid="leaflet-map-container"
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#1a202c'
              }}
            />

            {!leafletLoaded && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(15, 23, 42, 0.7)',
                  color: '#94a3b8',
                  fontSize: '14px',
                  zIndex: 500
                }}
              >
                Loading interactive map...
              </div>
            )}

            {/* Floating "📍 My Location" GPS Button */}
            <button
              type="button"
              onClick={handleLocateMe}
              disabled={isLocating}
              aria-label="Use My GPS Location"
              title="Fly to My Location"
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px',
                zIndex: 800,
                backgroundColor: '#0f172a',
                color: isLocating ? '#94a3b8' : '#38bdf8',
                border: '1px solid rgba(56, 189, 248, 0.4)',
                borderRadius: '50px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
              }}
            >
              <span>📍</span>
              <span>{isLocating ? 'Locating...' : 'My Location'}</span>
            </button>
          </>
        )}
      </div>

      {/* 4. Bottom Action Bar */}
      <div
        style={{
          padding: '14px 16px',
          background: 'var(--panel-bg, #0f172a)',
          borderTop: '1px solid var(--border-color, rgba(255,255,255,0.1))',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          flexShrink: 0
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'rgba(255,255,255,0.04)',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-mute, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Selected Coordinates ({String(format).toUpperCase()})
            </div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--primary-color, #38bdf8)', fontFamily: 'monospace', marginTop: '1px' }}>
              {formatCoordinates(currentCoords.lat, currentCoords.lng, format)}
            </div>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-mute, #64748b)' }}>
            Drag pin or tap map
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px',
              color: 'var(--text-main, #cbd5e1)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              flex: 2,
              padding: '12px',
              background: 'var(--primary-gradient, var(--primary-color, #10b981))',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
            }}
          >
            Set Location
          </button>
        </div>
      </div>
    </div>
  );
}
